import { useEffect, useState } from "react";
import axios from "axios";
import { Download, XCircle } from "lucide-react";
import { API, fmtErr, money } from "../lib/pos";
import { DateRangeField } from "../components/DateField";

export function RefundLedger() {
  const today = () => new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ count: 0, total_refund: 0, by_method: {}, by_cashier: {}, by_voider: {}, rows: [] });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (search.trim()) params.set("q", search.trim());
      const r = await axios.get(`${API}/refunds?${params.toString()}`);
      setData(r.data);
    } catch (e) { alert(fmtErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [start, end]);

  const applyPreset = (which) => {
    const t = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (which === "today") { setStart(iso(t)); setEnd(iso(t)); }
    else if (which === "yesterday") { const y = new Date(t); y.setDate(t.getDate() - 1); setStart(iso(y)); setEnd(iso(y)); }
    else if (which === "7d") { const s = new Date(t); s.setDate(t.getDate() - 6); setStart(iso(s)); setEnd(iso(t)); }
    else if (which === "30d") { const s = new Date(t); s.setDate(t.getDate() - 29); setStart(iso(s)); setEnd(iso(t)); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  const exportCsv = () => {
    const header = ["Date", "Order #", "Order ID", "Table", "Refund amount", "Method", "Cashier (took order)", "Voided by", "Reason"];
    const lines = [header.join(",")].concat(data.rows.map((r) => [
      fmtTime(r.created_at),
      String(r.order_number || ""),
      r.order_id || "",
      r.table || (r.order_type === "table" ? "Dine-in" : "Takeaway"),
      (r.refund_amount || 0).toFixed(2),
      (r.refund_method || "").toUpperCase(),
      r.cashier_name || "",
      r.cancelled_by_name || "",
      (r.reason || "").replace(/[",\n]/g, " "),
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `refund-ledger-${start}-to-${end}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const term = search.trim().toLowerCase();
  const rows = !term ? data.rows : data.rows.filter((r) => (
    String(r.order_number || "").includes(term) ||
    (r.order_id || "").toLowerCase().includes(term) ||
    (r.reason || "").toLowerCase().includes(term) ||
    (r.cashier_name || "").toLowerCase().includes(term) ||
    (r.cancelled_by_name || "").toLowerCase().includes(term)
  ));

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER TOOLS / AUDIT</p>
          <h1>Refund ledger</h1>
          <p className="muted">Every void of a <b style={{ color: "#fff" }}>paid</b> bill — reason, cashier who took it, staff who cancelled it, refund method, timestamp. Perfect for shift audits.</p>
        </div>
        <button data-testid="export-refunds-csv-button" className="outline" onClick={exportCsv} disabled={rows.length === 0}>
          <Download size={14} style={{ marginRight: 6 }} />Export CSV
        </button>
      </div>

      <div className="panel" data-testid="refunds-filters">
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div className="menu-search" style={{ flex: 1, minWidth: 240 }}>
            <input
              data-testid="refund-search-input"
              type="search"
              placeholder="Search by order #, reason, cashier or void-er…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button data-testid="refund-search-clear" className="search-clear" onClick={() => setSearch("")}>×</button>}
          </div>
          <label className="field" style={{ marginBottom: 0 }}>Range<DateRangeField start={start} end={end} onChange={({ start: s, end: e }) => { setStart(s); setEnd(e); }} testPrefix="refund" /></label>
          <div style={{ display: "flex", gap: 6 }}>
            <button data-testid="refund-preset-today" className="outline" onClick={() => applyPreset("today")}>Today</button>
            <button data-testid="refund-preset-yesterday" className="outline" onClick={() => applyPreset("yesterday")}>Yesterday</button>
            <button data-testid="refund-preset-7d" className="outline" onClick={() => applyPreset("7d")}>Last 7d</button>
            <button data-testid="refund-preset-30d" className="outline" onClick={() => applyPreset("30d")}>Last 30d</button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="kpi-strip" data-testid="refund-kpis" style={{ marginTop: 14 }}>
        <div className="kpi">
          <span className="eyebrow">TOTAL REFUND</span>
          <b data-testid="kpi-total">{money(data.total_refund)}</b>
          <small>{data.count} void{data.count === 1 ? "" : "s"}</small>
        </div>
        <div className="kpi">
          <span className="eyebrow">TOP METHOD</span>
          {(() => {
            const best = Object.entries(data.by_method).sort((a, b) => b[1] - a[1])[0];
            return best
              ? (<><b data-testid="kpi-top-method">{best[0].toUpperCase()}</b><small>{money(best[1])}</small></>)
              : (<><b>—</b><small>no refunds</small></>);
          })()}
        </div>
        <div className="kpi">
          <span className="eyebrow">TOP CASHIER (VOIDED)</span>
          {(() => {
            const best = Object.entries(data.by_cashier).sort((a, b) => b[1] - a[1])[0];
            return best
              ? (<><b data-testid="kpi-top-cashier">{best[0]}</b><small>{money(best[1])}</small></>)
              : (<><b>—</b><small>—</small></>);
          })()}
        </div>
        <div className="kpi">
          <span className="eyebrow">TOP VOIDER</span>
          {(() => {
            const best = Object.entries(data.by_voider).sort((a, b) => b[1] - a[1])[0];
            return best
              ? (<><b data-testid="kpi-top-voider">{best[0]}</b><small>{best[1]} void{best[1] === 1 ? "" : "s"}</small></>)
              : (<><b>—</b><small>—</small></>);
          })()}
        </div>
      </div>

      {loading ? <p className="muted" style={{ marginTop: 14 }}>Loading ledger…</p> : (
        <div className="menu-table refund-table" style={{ marginTop: 14 }}>
          <div className="table-head">
            <span>WHEN</span><span>ORDER</span><span>AMOUNT</span><span>METHOD</span><span>CASHIER</span><span>VOIDED BY</span><span>REASON</span>
          </div>
          {rows.length === 0 && (
            <div className="empty" style={{ minHeight: 140 }}>
              <XCircle size={26} />
              <b>No refunds in range</b>
              <span>Clean audit — no paid bills voided</span>
            </div>
          )}
          {rows.map((r) => (
            <div className="menu-line" key={r.id} data-testid={`refund-row-${r.id}`} onClick={() => setDetail(r)} style={{ cursor: "pointer" }}>
              <span style={{ fontSize: 11 }}>{fmtTime(r.created_at)}</span>
              <div>
                <b style={{ color: "var(--red)" }}>#{String(r.order_number || "").padStart(4, "0")}</b>
                <small>{(r.order_id || "").slice(-8)} · {r.table ? `Table ${r.table}` : "Takeaway"}</small>
              </div>
              <strong>-{money(r.refund_amount)}</strong>
              <span><b className={`method-pill method-${r.refund_method || "n"}`}>{(r.refund_method || "—").toUpperCase()}</b></span>
              <span style={{ fontSize: 11 }}>{r.cashier_name || "—"}</span>
              <span style={{ fontSize: 11 }}>{r.cancelled_by_name || "—"}</span>
              <span style={{ fontSize: 11, color: "var(--muted)" }}>{r.reason || "—"}</span>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setDetail(null)}>
          <div className="modal" style={{ width: "min(500px, 94vw)", maxHeight: "88vh", overflowY: "auto" }} data-testid="refund-detail-modal">
            <div className="section-line">
              <h2>Refund · #{String(detail.order_number || "").padStart(4, "0")}</h2>
              <button data-testid="close-refund-detail" onClick={() => setDetail(null)}>×</button>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>{fmtTime(detail.created_at)} · {detail.table ? `Table ${detail.table}` : "Takeaway"}</p>
            <div className="row-kv"><span>Refund amount</span><b>-{money(detail.refund_amount)}</b></div>
            <div className="row-kv"><span>Method</span><b>{(detail.refund_method || "—").toUpperCase()}</b></div>
            <div className="row-kv"><span>Cashier who took order</span><b>{detail.cashier_name || "—"}</b></div>
            <div className="row-kv"><span>Voided by</span><b>{detail.cancelled_by_name || "—"}</b></div>
            <div className="row-kv"><span>Reason</span><b style={{ maxWidth: "60%", textAlign: "right" }}>{detail.reason || "—"}</b></div>
            <hr />
            <p className="eyebrow" style={{ marginTop: 4 }}>ITEMS REFUNDED</p>
            <ul className="detail-items">
              {(detail.lines || []).map((l, i) => (
                <li key={i}>
                  <span><b>{l.quantity}× {l.item_name}</b><em> ({l.category})</em></span>
                  <strong>{money(l.amount)}</strong>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}


